/**
 * The member's notes: the legacy Notes screens, on this API.
 *
 * **View** is a list of cards, one per note — its title, then only the rows
 * that were filled in, because a note under one title answers a different part
 * of the form than a note under another and blank rows say nothing. Deleting
 * marks a card; Save commits, Close forgets, as legacy had it.
 *
 * **Add / Edit** is the note form, opened from the button beside the member or
 * from a card's pencil.
 *
 * Ported from `legacy/frontend/src/apps/mrm/components/MrmRowDetailView/
 * MrmNotes`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Pencil, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { Field, Form, Input } from "@/components/ui/field";
import { OptionSelect } from "@/components/ui/option-select";
import { Spinner } from "@/components/ui/spinner";
import { api, errorMessage, ifMatch } from "@/services/api";
import { titleCase } from "@/components/member-attachments";
import type { TranslationKey } from "@/constants/i18n";
import { useCan } from "@/utils/permissions";
import { useTranslation } from "@/store/locale";

export interface Note {
  id: number;
  title: string;
  note: string;
  updated_at?: string;
  [field: string]: unknown;
}

const listKey = (memberId: number) => [`/members/${memberId}/notes`] as const;

export function useNotes(memberId: number) {
  return useQuery({
    queryKey: listKey(memberId),
    queryFn: async ({ signal }) =>
      (await api.get<Note[]>(`/members/${memberId}/notes`, { signal })).data,
  });
}

/**
 * The keys on a note that hold an id rather than a word, and the catalogue each
 * is answered from. A note points at its rows rather than copying their text —
 * the one reversal in this schema — so a card that shows the id shows a number
 * nobody recognises.
 */
const NAMED_BY: Record<string, "reasons" | "promo-codes"> = {
  reason_id: "reasons",
  approved_by_id: "reasons",
  endorsed_by_id: "reasons",
  left_at_id: "reasons",
  complaint_reason_id: "reasons",
  promo_code_id: "promo-codes",
};

/** The ids these notes actually name, so only those labels are asked for. */
const numbersOn = (notes: Note[], keys: string[]): number[] => {
  const found = new Set<number>();
  for (const note of notes) {
    for (const key of keys) {
      const value = Number(note[key]);
      if (Number.isFinite(value)) found.add(value);
    }
  }
  return [...found].sort((a, b) => a - b);
};

const idsOn = (notes: Note[], catalogue: "reasons" | "promo-codes"): number[] =>
  numbersOn(
    notes,
    Object.keys(NAMED_BY).filter((key) => NAMED_BY[key] === catalogue),
  );

/**
 * Labels for exactly these ids.
 *
 * `/compact` takes `id` repeated and answers with just those — so a card names
 * what it shows without pulling down a catalogue to find four words in it, and
 * a reason since retired is still named on the note that used it.
 */
const labelsFor = async (catalogue: string, ids: number[], signal?: AbortSignal) =>
  (
    await api.get<{ id: number; name: string }[]>(`/${catalogue}/compact`, {
      params: new URLSearchParams(ids.map((id) => ["id", String(id)])),
      ...(signal ? { signal } : {}),
    })
  ).data.map((one) => ({ value: one.id, label: one.name }));

/** One labelled line of a card, in the order legacy showed them. */
const ROWS: { key: string; label: TranslationKey }[] = [
  { key: "note", label: "hist.note" },
  { key: "reason_id", label: "hist.reason" },
  { key: "club_id", label: "hist.club" },
  { key: "canvasser_employee_name", label: "hist.canvasser" },
  { key: "promo_code_id", label: "hist.promoCode" },
  { key: "requested_by_id", label: "hist.requestedBy" },
  { key: "approved_by_id", label: "hist.approvedBy" },
  { key: "endorsed_by_employee_name", label: "hist.endorsedBy" },
  { key: "endorsed_to_employee_name", label: "hist.endorsedTo" },
  { key: "left_at_id", label: "hist.leftAt" },
  { key: "complaint_reason_id", label: "hist.complaintReason" },
  { key: "created_by_id", label: "hist.addedBy" },
  { key: "created_at", label: "hist.added" },
];

export function ViewNotesModal({
  memberId,
  accountNumber,
  onClose,
  onEdit,
}: {
  memberId: number;
  accountNumber?: string | undefined;
  onClose: () => void;
  onEdit: (note: Note) => void;
}) {
  const t = useTranslation();
  const allow = useCan();
  const queryClient = useQueryClient();
  const rows = useNotes(memberId);
  const [marked, setMarked] = useState<ReadonlySet<number>>(new Set());

  const list = useMemo(() => rows.data ?? [], [rows.data]);

  // The ids a card shows as names, every one of them asked for by id. A
  // `/compact` page is ordered by name, so a blanket page of two hundred said
  // nothing about whether the account that wrote *this* note is on it: the
  // administrator is id 1, sits outside the first two hundred names, and Added
  // By read "1". One small request each, and never a catalogue.
  const wantedUsers = useMemo(() => numbersOn(list, ["created_by_id"]), [list]);
  const wantedClubs = useMemo(() => numbersOn(list, ["club_id", "requested_by_id"]), [list]);
  const users = useQuery({
    queryKey: ["/users/compact", wantedUsers],
    queryFn: ({ signal }) => labelsFor("users", wantedUsers, signal),
    enabled: wantedUsers.length > 0,
    staleTime: 300_000,
  });
  const clubs = useQuery({
    queryKey: ["/clubs/compact", wantedClubs],
    queryFn: ({ signal }) => labelsFor("clubs", wantedClubs, signal),
    enabled: wantedClubs.length > 0,
    staleTime: 300_000,
  });
  const shown = list.filter((note) => !marked.has(note.id));

  const wantedReasons = useMemo(() => idsOn(list, "reasons"), [list]);
  const wantedPromos = useMemo(() => idsOn(list, "promo-codes"), [list]);
  const reasons = useQuery({
    queryKey: ["/reasons/compact", wantedReasons],
    queryFn: ({ signal }) => labelsFor("reasons", wantedReasons, signal),
    enabled: wantedReasons.length > 0,
    staleTime: 300_000,
  });
  const promos = useQuery({
    queryKey: ["/promo-codes/compact", wantedPromos],
    queryFn: ({ signal }) => labelsFor("promo-codes", wantedPromos, signal),
    enabled: wantedPromos.length > 0,
    staleTime: 300_000,
  });

  const remove = useMutation({
    mutationFn: async () => {
      for (const id of marked) {
        const note = list.find((one) => one.id === id);
        await api.delete(`/members/${memberId}/notes/${id}`, ifMatch(note));
      }
    },
    onSuccess: async () => {
      toast.success(t("note.deleted"));
      await queryClient.invalidateQueries({ queryKey: listKey(memberId) });
      setMarked(new Set());
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, t("note.deleteFailed"))),
  });

  const name = (options: { value: number; label: string }[] | undefined, id: unknown) =>
    options?.find((one) => one.value === Number(id))?.label ?? String(id);

  /**
   * One line of a card, as a reader should see it.
   *
   * The note names its own references now — `reason_name` beside `reason_id`,
   * and so on — which is what stops a card reading differently for different
   * people. The lookups below it are a fallback, for a deployment whose API
   * has not caught up: they ask catalogues that sit behind their own view
   * rights, and a Supervisor holds none of them, so what they produce is a
   * number. The server's answer is the one to trust when there is one.
   */
  const show = (key: string, value: unknown, note: Note): string | null => {
    if (value === null || value === undefined || value === "") return null;
    const given = note[`${key.replace(/_id$/, "")}_name`];
    if (typeof given === "string" && given !== "") return given;
    if (key === "created_by_id") return name(users.data, value);
    if (key === "club_id" || key === "requested_by_id") return name(clubs.data, value);
    if (NAMED_BY[key] === "reasons") return name(reasons.data, value);
    if (NAMED_BY[key] === "promo-codes") return name(promos.data, value);
    if (key === "created_at" && typeof value === "string")
      return format(new Date(value), "MM/dd/yyyy h:mm a");
    return String(value).replace(/_/g, " ");
  };

  const toggle = (id: number) =>
    setMarked((was) => {
      const next = new Set(was);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const canEdit = allow("change__member_note");
  const canDelete = allow("delete__member_note");
  const title = accountNumber ? `${t("hist.notes")} — ${accountNumber}` : t("hist.notes");

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.close")}
          </Button>
          <Button
            disabled={marked.size === 0}
            loading={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {t("rp.save")}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 pb-2">
        {rows.isPending ? (
          <div className="grid place-items-center py-10">
            <Spinner />
          </div>
        ) : shown.length === 0 ? (
          <p className="text-fg-muted text-callout py-10 text-center">{t("note.none")}</p>
        ) : (
          shown.map((note) => (
            <article
              key={note.id}
              className="border-line bg-surface-sunken rounded-xl border p-4"
            >
              <header className="hairline-b flex items-center justify-between gap-3 pb-2">
                <h3 className="text-fg text-callout font-medium">{titleCase(note.title)}</h3>
                <div className="flex items-center gap-1">
                  {canEdit ? (
                    <button
                      type="button"
                      title={t("common.edit")}
                      onClick={() => onEdit(note)}
                      className="text-fg-muted hover:bg-surface-inset hover:text-fg grid size-7 place-items-center rounded-lg transition-colors"
                    >
                      <Pencil className="size-3.5" aria-hidden />
                      <span className="sr-only">{t("common.edit")}</span>
                    </button>
                  ) : null}
                  {canDelete ? (
                    <button
                      type="button"
                      title={t("common.delete")}
                      onClick={() => toggle(note.id)}
                      className="text-fg-muted hover:bg-danger-soft hover:text-danger grid size-7 place-items-center rounded-lg transition-colors"
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                      <span className="sr-only">{t("common.delete")}</span>
                    </button>
                  ) : null}
                </div>
              </header>
              <dl className="text-callout grid gap-1.5 pt-2 sm:grid-cols-[11rem_minmax(0,1fr)]">
                {ROWS.map(({ key, label }) => {
                  const value = show(key, note[key], note);
                  return value === null ? null : (
                    <div key={key} className="contents">
                      <dt className="text-fg-muted">{t(label)}</dt>
                      <dd className="text-fg min-w-0 break-words">{value}</dd>
                    </div>
                  );
                })}
              </dl>
            </article>
          ))
        )}

        {marked.size > 0 ? (
          <p className="text-fg-muted text-footnote">
            {t("note.markedForDeletion").replace("{count}", String(marked.size))}{" "}
            <button
              type="button"
              onClick={() => setMarked(new Set())}
              className="text-accent font-medium hover:underline"
            >
              {t("common.undo")}
            </button>
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * The note form: what it asks depends on the title, as legacy's did.
 *
 * A note under one title answers a different question from a note under
 * another — where a card was left, who endorsed a complimentary membership,
 * which canvasser signed a member up — so the form is three forms behind one
 * picker rather than one form with two thirds of it greyed out.
 *
 * Endorsement goes a level further: the reason decides what else is asked, and
 * the three reasons that ask for something are matched by what they say rather
 * than by id, because the list is a catalogue an administrator maintains.
 */

const TITLES = ["pre_opening", "endorsement", "complimentary"] as const;

const choicesOf = (values: readonly string[]) =>
  values.map((value) => ({ value, label: titleCase(value) }));

/** Every list the form offers, as the note's own route answers it. */
interface Options {
  /** Optional on purpose: see `listed` below — a missing key is an empty list. */
  reasons?: Record<string, { id: number; name: string | null }[] | undefined>;
  clubs?: { id: number; name: string | null }[];
  promo_codes?: { id: number; name: string | null }[];
}

const choices = (rows: { id: number; name: string | null }[] | undefined) =>
  (rows ?? []).map((one) => ({ value: String(one.id), label: one.name ?? "" }));

/** What an endorsement reason asks for next, from what the reason says. */
type Follows = "complaint" | "promo" | "left_at" | null;
const follows = (label: string | undefined): Follows => {
  const said = (label ?? "").toLowerCase();
  if (said.includes("complaint")) return "complaint";
  if (said.includes("promo")) return "promo";
  if (said.includes("left")) return "left_at";
  return null;
};

/** Every field the three forms between them ask for, each always a string. */
const FIELDS = [
  "title",
  "club_id",
  "canvasser_employee_no",
  "canvasser_employee_name",
  "reason_id",
  "complaint_reason_id",
  "promo_code_id",
  "left_at_id",
  "endorsed_by_employee_no",
  "endorsed_by_employee_name",
  "endorsed_to_employee_no",
  "endorsed_to_employee_name",
  "requested_by_id",
  "endorsed_by_id",
  "approved_by_id",
] as const;

type FieldName = (typeof FIELDS)[number];
type Values = Record<FieldName, string>;

export function NoteFormModal({
  memberId,
  note,
  approvedById,
  onClose,
}: {
  memberId: number;
  /** `null` for a new note. */
  note: Note | null;
  /**
   * Who the membership itself was approved by, from the member's own record.
   *
   * A Complimentary note is the record of a sign-off the tier change already
   * asked for, so the form opens with that name in it rather than making
   * somebody look it up and key it again. A note being edited keeps its own.
   */
  approvedById?: number | null | undefined;
  onClose: () => void;
}) {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Values>(() => {
    const start = Object.fromEntries(
      FIELDS.map((name) => [name, str(note?.[name])]),
    ) as unknown as Values;
    if (!note && approvedById != null) start.approved_by_id = String(approvedById);
    return start;
  });
  const [error, setError] = useState<string | null>(null);

  const set = (name: FieldName, value: string) =>
    setValues((current) => ({ ...current, [name]: value }));

  // Every picker on the form, in one request to the note's own route.
  //
  // Each used to fetch its own list — the reasons from `/reasons/compact`, the
  // clubs and promos from theirs. Those are the Configuration screens' lists
  // and ask for the Configuration screens' rights: `view__reason`,
  // `view__club`, `view__promo_code`, none of which a Supervisor holds. So the
  // form opened with an empty Reason, a Club saying "No matches", and an
  // Approved By showing the raw id. Writing a note and administering the lists
  // a note draws from are different things.
  //
  // Not gated on the title either: the lists are small, they are wanted the
  // moment the title changes, and a picker that appears a beat after the field
  // it belongs to reads as a form still loading.
  const options = useQuery({
    queryKey: [`/members/${memberId}/notes/options`],
    queryFn: async ({ signal }) =>
      (await api.get<Options>(`/members/${memberId}/notes/options`, { signal })).data,
    staleTime: 300_000,
  });
  // Read defensively, the way `can()` reads a profile: an answer that is not
  // the shape it should be — an API that has not caught up, a request that
  // failed — must cost a picker its options, not take the dialog down with a
  // TypeError. Every one of these is a list or it is empty.
  const listed = (kind: string) => choices(options.data?.reasons?.[kind]);
  const endorsementReasons = { data: listed("endorsement") };
  const leftAtPlaces = { data: listed("left_card_at") };
  const complaintReasons = { data: listed("complaint") };
  const endorsedBy = { data: listed("complimentary_endorsed_by") };
  const approvedBy = { data: listed("complimentary_approved_by") };
  const clubChoices = choices(options.data?.clubs);
  const promoChoices = choices(options.data?.promo_codes);

  const asks = follows(
    (endorsementReasons.data ?? []).find((one) => one.value === values.reason_id)?.label,
  );

  /**
   * The employee names this title asks for that the directory has not answered.
   *
   * The number is typed and the name is looked up from it, and until the lookup
   * comes back the name field is empty — so a Pre Opening note saved quickly
   * enough went in naming a canvasser number and nobody, which is the half of
   * the pair the note has to still read by in five years. The name field is
   * `disabled`, so its `required` is not something the browser enforces and
   * the form submitted happily.
   *
   * Read from `shownFields` rather than listed here, so the endorsement that
   * names two employees is covered by the same rule as the one that names one.
   */
  const unnamed = shownFields(values.title, asks).filter(
    (name) => name.endsWith("_employee_name") && values[name].trim() === "",
  );

  const save = useMutation({
    mutationFn: () => {
      // Only what this title asks for goes over. A field the form did not show
      // is sent as null rather than left out, so retitling a note clears what
      // belonged to the title it used to have.
      const asked = new Set<FieldName>(shownFields(values.title, asks));
      const body = Object.fromEntries(
        FIELDS.filter((name) => name !== "title").map((name) => {
          const value = values[name];
          return [
            name,
            !asked.has(name) || value === "" ? null : numeric(name) ? Number(value) : value,
          ];
        }),
      );
      const payload = { ...body, title: values.title };
      return note
        ? api.patch(`/members/${memberId}/notes/${note.id}`, payload, ifMatch(note))
        : api.post(`/members/${memberId}/notes`, payload);
    },
    onSuccess: async () => {
      toast.success(
        t(note ? "rp.updated" : "rp.created").replace("{singular}", t("hist.note")),
      );
      await queryClient.invalidateQueries({ queryKey: listKey(memberId) });
      onClose();
    },
    onError: (err) =>
      setError(errorMessage(err, t("rp.saveFailed").replace("{singular}", t("hist.note")))),
  });

  const formId = "note-form";
  const title = t(note ? "rp.editSingular" : "rp.addSingular").replace(
    "{singular}",
    t("hist.note"),
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          {/* Not saveable until the number has been answered for. The reason
              is on the button rather than only in its greyness: a disabled
              control with nothing to say is a dead end, and the field it is
              waiting on is two rows up. */}
          <Button
            type="submit"
            form={formId}
            loading={save.isPending}
            disabled={unnamed.length > 0}
            title={unnamed.length > 0 ? t("hist.employeeNotFound") : undefined}
          >
            {t("rp.save")}
          </Button>
        </>
      }
    >
      <Form
        id={formId}
        className="grid gap-4 pb-2"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <Field label={t("hist.title")} htmlFor="note-title" required>
          <OptionSelect
            id="note-title"
            value={values.title}
            onChange={(next) => set("title", next)}
            required
            placeholder={t("rp.select").replace("{label}", t("hist.title").toLowerCase())}
            choices={choicesOf(TITLES)}
          />
        </Field>

        {values.title === "pre_opening" ? (
          <>
            <Field label={t("hist.club")} htmlFor="note-club" required>
              {/* The same list the rest of the form reads, so a Supervisor
                  does not get "No matches" from a catalogue they may not
                  browse. */}
              <OptionSelect
                id="note-club"
                value={values.club_id}
                onChange={(next) => set("club_id", next)}
                required
                choices={clubChoices}
                placeholder={t("rp.select").replace("{label}", t("hist.club").toLowerCase())}
              />
            </Field>
            <EmployeePair
              label={t("hist.canvasserEmployeeNo")}
              name="canvasser_employee_no"
              fills="canvasser_employee_name"
              values={values}
              set={set}
              required
            />
          </>
        ) : null}

        {values.title === "endorsement" ? (
          <>
            <Field label={t("hist.reason")} htmlFor="note-reason" required>
              <OptionSelect
                id="note-reason"
                value={values.reason_id}
                onChange={(next) => set("reason_id", next)}
                required
                placeholder={t("rp.select").replace("{label}", t("hist.reason").toLowerCase())}
                choices={endorsementReasons.data ?? []}
              />
            </Field>

            {asks === "complaint" ? (
              <Field label={t("hist.complaintReason")} htmlFor="note-complaint">
                <OptionSelect
                  id="note-complaint"
                  value={values.complaint_reason_id}
                  onChange={(next) => set("complaint_reason_id", next)}
                  placeholder={t("rp.select").replace(
                    "{label}",
                    t("hist.complaintReason").toLowerCase(),
                  )}
                  choices={complaintReasons.data ?? []}
                />
              </Field>
            ) : null}

            {asks === "promo" ? (
              <Field label={t("hist.promoCode")} htmlFor="note-promo">
                <OptionSelect
                  id="note-promo"
                  value={values.promo_code_id}
                  onChange={(next) => set("promo_code_id", next)}
                  choices={promoChoices}
                  placeholder={t("rp.select").replace(
                    "{label}",
                    t("hist.promoCode").toLowerCase(),
                  )}
                />
              </Field>
            ) : null}

            {asks === "left_at" ? (
              <Field label={t("hist.leftAt")} htmlFor="note-left-at" required>
                <OptionSelect
                  id="note-left-at"
                  value={values.left_at_id}
                  onChange={(next) => set("left_at_id", next)}
                  required
                  placeholder={t("rp.select").replace(
                    "{label}",
                    t("hist.leftAt").toLowerCase(),
                  )}
                  choices={leftAtPlaces.data ?? []}
                />
              </Field>
            ) : null}

            {/* Only a card left behind: that is the reason where a card
                changes hands, so it is the one that asks who passed it to
                whom. The other reasons ask nothing of the kind. */}
            {asks === "left_at" ? (
              <>
                <EmployeePair
                  label={t("hist.endorsedByEmployeeNo")}
                  name="endorsed_by_employee_no"
                  fills="endorsed_by_employee_name"
                  values={values}
                  set={set}
                  required
                />
                <EmployeePair
                  label={t("hist.endorsedToEmployeeNo")}
                  name="endorsed_to_employee_no"
                  fills="endorsed_to_employee_name"
                  values={values}
                  set={set}
                  required
                />
              </>
            ) : null}
          </>
        ) : null}

        {values.title === "complimentary" ? (
          // Three across, as legacy laid them out: one question asked three
          // ways, and each answer is one word.
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label={t("hist.requestedBy")} htmlFor="note-requested">
              <OptionSelect
                id="note-requested"
                value={values.requested_by_id}
                onChange={(next) => set("requested_by_id", next)}
                choices={clubChoices}
                placeholder=""
              />
            </Field>
            <Field label={t("hist.endorsedBy")} htmlFor="note-endorsed">
              <OptionSelect
                id="note-endorsed"
                value={values.endorsed_by_id}
                onChange={(next) => set("endorsed_by_id", next)}
                placeholder=""
                choices={endorsedBy.data ?? []}
              />
            </Field>
            <Field label={t("hist.approvedBy")} htmlFor="note-approved">
              <OptionSelect
                id="note-approved"
                value={values.approved_by_id}
                onChange={(next) => set("approved_by_id", next)}
                placeholder=""
                choices={approvedBy.data ?? []}
              />
            </Field>
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="text-danger text-callout">
            {error}
          </p>
        ) : null}
      </Form>
    </Modal>
  );
}

/**
 * An employee number beside the name it belongs to.
 *
 * The clerk has the number; the name is looked up from it and the field
 * holding it is read-only, so a name can never disagree with the number next
 * to it. A number that names nobody empties the name rather than leaving the
 * last one standing.
 */
/**
 * An employee number and the name it belongs to, side by side.
 *
 * The number is what a clerk has and what the note is filled in from; the name
 * is what the note has to still read as years later, which is why both are
 * kept. Typing the number asks who it belongs to — the HR directory first and
 * the accounts here second, which is the same question the Add Employee form
 * asks. It used to ask only the accounts, so a canvasser who had never been
 * given one could not be named without typing the name by hand.
 *
 * The name field stays read-only either way: the directory is authoritative
 * about who a number belongs to, and a name typed over it would be a second
 * answer to a question that has one.
 */
function EmployeePair({
  label,
  name,
  fills,
  values,
  set,
  required = false,
}: {
  label: string;
  name: FieldName;
  fills: FieldName;
  values: Values;
  set: (name: FieldName, value: string) => void;
  required?: boolean;
}) {
  const t = useTranslation();
  const typed = values[name];

  useEffect(() => {
    const number = typed.trim();
    if (!number) {
      set(fills, "");
      return;
    }
    const controller = new AbortController();
    // Debounced: this runs on every keystroke of a number being typed.
    const timer = setTimeout(() => {
      void api
        .get<{ full_name: string }>(`/snr/${encodeURIComponent(number)}/name`, {
          signal: controller.signal,
        })
        .then(({ data }) => set(fills, data.full_name))
        // No such employee is an answer, not a failure.
        .catch(() => set(fills, ""));
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed]);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label={label} htmlFor={name} required={required}>
        <Input
          id={name}
          value={typed}
          required={required}
          inputMode="numeric"
          placeholder={t("hist.employeeNoHint")}
          onChange={(event) => set(name, event.target.value)}
        />
      </Field>
      <Field label={t("hist.employeeName")} htmlFor={fills} required={required}>
        <Input id={fills} value={values[fills]} disabled readOnly />
      </Field>
    </div>
  );
}

/**
 * Every field this title asks for; anything else is cleared on save.
 *
 * An endorsement is a title and a reason until the reason is chosen, because
 * which reason it is decides what the rest of the form is for — and only a
 * card left behind asks who endorsed it to whom, since that is the reason
 * where a card changes hands.
 */
function shownFields(title: string, asks: Follows): FieldName[] {
  if (title === "pre_opening") {
    return ["club_id", "canvasser_employee_no", "canvasser_employee_name"];
  }
  if (title === "endorsement") {
    if (asks === "complaint") return ["reason_id", "complaint_reason_id"];
    if (asks === "promo") return ["reason_id", "promo_code_id"];
    if (asks === "left_at") {
      return [
        "reason_id",
        "left_at_id",
        "endorsed_by_employee_no",
        "endorsed_by_employee_name",
        "endorsed_to_employee_no",
        "endorsed_to_employee_name",
      ];
    }
    return ["reason_id"];
  }
  if (title === "complimentary") {
    return ["requested_by_id", "endorsed_by_id", "approved_by_id"];
  }
  return [];
}

/** The fields the API takes as ids rather than words. */
const numeric = (name: string) => name.endsWith("_id");

const str = (value: unknown) => (value === null || value === undefined ? "" : String(value));
